// License.tsx
import React, { useState, useEffect } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, Loader2, RotateCw, Eye, EyeOff } from "lucide-react";
import {
  getLicenseInfo,
  saveLicense,
  getTavilyApiKey,
  saveTavilyApiKey,
  getCanliiApiKey,
  saveCanliiApiKey,
  getAlphaVantageApiKey,
  saveAlphaVantageApiKey
} from "../../../../service/apiService";

import { useUser } from "../../../../context/UserContext";
import { toast } from "sonner";

interface LicenseInfo {
  logical_license_id: string | null;
  validity: {
    Begin?: string;
    End?: string;
  };
}

interface TavilyApiResponse {
  success: boolean;
  message: string;
  api_key?: string;
}

const LICENSE_ID_REGEX = /^l-[a-f0-9]{32}$/;

const License: React.FC = () => {
  // License Management State
  const [isEditing, setIsEditing] = useState(false);
  const [licenseId, setLicenseId] = useState("");
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo>({
    logical_license_id: null,
    validity: {},
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Tavily API Management State
  const [tavilyApiKey, setTavilyApiKey] = useState("");
  const [isLoadingTavily, setIsLoadingTavily] = useState(false);
  const [isSavingTavily, setIsSavingTavily] = useState(false);
  const [isEditingTavily, setIsEditingTavily] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [tavilyError, setTavilyError] = useState<string | null>(null);

  // Canlii API Management State
  const [canliiApiKey, setCanliiApiKey] = useState("");
  const [isLoadingCanlii, setIsLoadingCanlii] = useState(false);
  const [isSavingCanlii, setIsSavingCanlii] = useState(false);
  const [isEditingCanlii, setIsEditingCanlii] = useState(false);
  const [showCanliiApiKey, setShowCanliiApiKey] = useState(false);
  const [canliiError, setCanliiError] = useState<string | null>(null);

  // Alpha Vantage API Management State
  const [alphaVantageApiKey, setAlphaVantageApiKey] = useState("");
  const [isLoadingAlphaVantage, setIsLoadingAlphaVantage] = useState(false);
  const [isSavingAlphaVantage, setIsSavingAlphaVantage] = useState(false);
  const [isEditingAlphaVantage, setIsEditingAlphaVantage] = useState(false);
  const [showAlphaVantageApiKey, setShowAlphaVantageApiKey] = useState(false);
  const [alphaVantageError, setAlphaVantageError] = useState<string | null>(null);

  const { userId } = useUser();

  // Initial data loading
  useEffect(() => {
    if (!userId) return;
    fetchLicenseInfo();
    fetchTavilyApiKey();
    fetchCanliiApiKey();
    fetchAlphaVantageApiKey();
  }, [userId]);

  // License Management Functions
  const validateLicenseId = (id: string): boolean => {
    return LICENSE_ID_REGEX.test(id);
  };

  const handleLicenseIdChange = (value: string) => {
    setLicenseId(value);
    setValidationError(null);
  };

  const fetchLicenseInfo = async () => {
    try {
      setIsLoading(true);
      const info = await getLicenseInfo();
      setLicenseInfo(info);
      if (info.logical_license_id) {
        setLicenseId(info.logical_license_id);
      }
    } catch (error) {
      console.error("Error fetching license info:", error);
      toast.error("Failed to fetch license information");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setValidationError(null);
  };

  const handleSave = async () => {
    if (!licenseId.trim()) {
      setValidationError("License ID is required");
      toast.error("License ID is required");
      return;
    }

    if (!validateLicenseId(licenseId)) {
      setValidationError(
        "Invalid license ID format. Must start with 'l-' followed by 32 hexadecimal characters"
      );
      toast.error("Invalid license ID format");
      return;
    }

    try {
      setIsSaving(true);
      await saveLicense(licenseId);
      await fetchLicenseInfo();
      setIsEditing(false);
      setValidationError(null);
      toast.success("License saved successfully");
    } catch (error) {
      console.error("Error saving license:", error);
      toast.error("Failed to save license");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLicenseId(licenseInfo.logical_license_id || "");
    setIsEditing(false);
    setValidationError(null);
  };

  // Tavily API Management Functions
  const fetchTavilyApiKey = async () => {
    try {
      setIsLoadingTavily(true);
      const response = await getTavilyApiKey();
      if (response.success && response.api_key) {
        setTavilyApiKey(response.api_key);
      }
    } catch (error) {
      console.error("Error fetching Tavily API key:", error);
      toast.error("Failed to fetch Tavily API key");
    } finally {
      setIsLoadingTavily(false);
    }
  };

  const handleTavilyEdit = () => {
    setIsEditingTavily(true);
    setTavilyError(null);
  };

  const handleTavilySave = async () => {
    if (!tavilyApiKey.trim()) {
      setTavilyError("Tavily API key is required");
      toast.error("Tavily API key is required");
      return;
    }

    try {
      setIsSavingTavily(true);
      const response = await saveTavilyApiKey(tavilyApiKey);
      if (response.success) {
        setIsEditingTavily(false);
        setTavilyError(null);
        toast.success("Tavily API key saved successfully");
      } else {
        setTavilyError(response.message);
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error saving Tavily API key:", error);
      toast.error("Failed to save Tavily API key");
    } finally {
      setIsSavingTavily(false);
    }
  };

  const handleTavilyCancel = () => {
    setIsEditingTavily(false);
    setTavilyError(null);
    fetchTavilyApiKey();
  };

  // Canlii API Management Functions
  const fetchCanliiApiKey = async () => {
    try {
      setIsLoadingCanlii(true);
      const response = await getCanliiApiKey();
      if (response.success && response.api_key) {
        setCanliiApiKey(response.api_key);
      }
    } catch (error) {
      console.error("Error fetching Canlii API key:", error);
      toast.error("Failed to fetch Canlii API key");
    } finally {
      setIsLoadingCanlii(false);
    }
  };

  const handleCanliiEdit = () => {
    setIsEditingCanlii(true);
    setCanliiError(null);
  };

  const handleCanliiSave = async () => {
    if (!canliiApiKey.trim()) {
      setCanliiError("Canlii API key is required");
      toast.error("Canlii API key is required");
      return;
    }

    try {
      setIsSavingCanlii(true);
      const response = await saveCanliiApiKey(canliiApiKey);
      if (response.success) {
        setIsEditingCanlii(false);
        setCanliiError(null);
        toast.success("Canlii API key saved successfully");
      } else {
        setCanliiError(response.message);
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error saving Canlii API key:", error);
      toast.error("Failed to save Canlii API key");
    } finally {
      setIsSavingCanlii(false);
    }
  };

  const handleCanliiCancel = () => {
    setIsEditingCanlii(false);
    setCanliiError(null);
    fetchCanliiApiKey();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not set";
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Alpha Vantage API Management Functions
  const fetchAlphaVantageApiKey = async () => {
    try {
      setIsLoadingAlphaVantage(true);
      const response = await getAlphaVantageApiKey();
      if (response.success && response.api_key) {
        setAlphaVantageApiKey(response.api_key);
      }
    } catch (error) {
      console.error("Error fetching Alpha Vantage API key:", error);
      toast.error("Failed to fetch Alpha Vantage API key");
    } finally {
      setIsLoadingAlphaVantage(false);
    }
  };

  const handleAlphaVantageEdit = () => {
    setIsEditingAlphaVantage(true);
    setAlphaVantageError(null);
  };

  const handleAlphaVantageSave = async () => {
    if (!alphaVantageApiKey.trim()) {
      setAlphaVantageError("Alpha Vantage API key is required");
      toast.error("Alpha Vantage API key is required");
      return;
    }

    try {
      setIsSavingAlphaVantage(true);
      const response = await saveAlphaVantageApiKey(alphaVantageApiKey);
      if (response.success) {
        setIsEditingAlphaVantage(false);
        setAlphaVantageError(null);
        toast.success("Alpha Vantage API key saved successfully");
      } else {
        setAlphaVantageError(response.message);
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error saving Alpha Vantage API key:", error);
      toast.error("Failed to save Alpha Vantage API key");
    } finally {
      setIsSavingAlphaVantage(false);
    }
  };

  const handleAlphaVantageCancel = () => {
    setIsEditingAlphaVantage(false);
    setAlphaVantageError(null);
    fetchAlphaVantageApiKey();
  };

  return (
    <div className="space-y-4">
      {/* License Management Section */}
      <Disclosure as="div" className="py-2" defaultOpen>
        <DisclosureButton className="group flex w-full items-center justify-between">
          <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Kyva Subscription License
          </span>
          <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
        </DisclosureButton>
        <DisclosurePanel className="mt-4 text-sm text-gray-600">
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                <label htmlFor="licenseId" className="block text-sm font-medium text-gray-700">
                  License ID
                </label>
                <input
                  id="licenseId"
                  type="text"
                  value={licenseId}
                  onChange={(e) => handleLicenseIdChange(e.target.value)}
                  disabled={!isEditing || isSaving}
                  placeholder="e.g., l-9db29235f07840c6ba993cdebb1bfa13"
                  className={`mt-2 w-full px-4 py-2 text-sm text-gray-700 bg-white border 
                    ${validationError ? "border-red-500" : "border-gray-200"}
                    rounded-lg transition-all duration-200 
                    ${
                      isEditing
                        ? "focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200"
                        : "disabled:cursor-not-allowed disabled:bg-gray-200"
                    }`}
                />
                {validationError && (
                  <p className="mt-1 text-xs text-red-500">{validationError}</p>
                )}
              </div>

              {licenseInfo.validity && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    License Validity
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500">Valid From</span>
                        <p className="text-sm text-gray-700">
                          {formatDate(licenseInfo.validity.Begin)}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Valid Until</span>
                        <p className="text-sm text-gray-700">
                          {formatDate(licenseInfo.validity.End)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-4 space-x-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="inline-flex justify-center items-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isSaving}
                      className="inline-flex justify-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-200
                        hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="inline-flex justify-center py-2 px-4 border border-transparent
                      shadow-sm text-sm font-medium rounded-md text-white bg-violet-600
                      hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                      focus:ring-violet-500"
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </DisclosurePanel>
      </Disclosure>

      {/* Internet License Management Section */}
      <Disclosure as="div" className="py-2" defaultOpen>
        <DisclosureButton className="group flex w-full items-center justify-between">
          <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
            Tavily Internet License
          </span>
          <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
        </DisclosureButton>
        <DisclosurePanel className="mt-4 text-sm text-gray-600">
          {isLoadingTavily ? (
            <div className="flex items-center space-x-2">
              <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                <label htmlFor="tavilyApiKey" className="block text-sm font-medium text-gray-700">
                  License ID
                </label>
                <div className="relative mt-2">
                  <input
                    id="tavilyApiKey"
                    type={showApiKey ? "text" : "password"}
                    value={tavilyApiKey}
                    onChange={(e) => setTavilyApiKey(e.target.value)}
                    disabled={!isEditingTavily || isSavingTavily}
                    placeholder="Enter Tavily API key"
                    className={`w-full px-4 py-2 pr-10 text-sm text-gray-700 bg-white border 
                      ${tavilyError ? "border-red-500" : "border-gray-200"}
                      rounded-lg transition-all duration-200 
                      ${
                        isEditingTavily
                          ? "focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200"
                          : "disabled:cursor-not-allowed disabled:bg-gray-200"
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
                {tavilyError && (
                  <p className="mt-1 text-xs text-red-500">{tavilyError}</p>
                )}
              </div>

              <div className="flex justify-end mt-4 space-x-2">
                {isEditingTavily ? (
                  <>
                    <button
                      onClick={handleTavilySave}
                      disabled={isSavingTavily}
                      className="inline-flex justify-center items-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-white bg-violet-600
                        hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingTavily ? (
                        <>
                          <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                    <button
                      onClick={handleTavilyCancel}
                      disabled={isSavingTavily}
                      className="inline-flex justify-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-200
                        hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleTavilyEdit}
                    className="inline-flex justify-center py-2 px-4 border border-transparent
                      shadow-sm text-sm font-medium rounded-md text-white bg-violet-600
                      hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                      focus:ring-violet-500"
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </DisclosurePanel>
      </Disclosure>

      {/* Canlii License Management Section */}
      <Disclosure as="div" className="py-2" defaultOpen>
        <DisclosureButton className="group flex w-full items-center justify-between">
          <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
            Canlii License
          </span>
          <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
        </DisclosureButton>
        <DisclosurePanel className="mt-4 text-sm text-gray-600">
          {isLoadingCanlii ? (
            <div className="flex items-center space-x-2">
              <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                <label htmlFor="canliiApiKey" className="block text-sm font-medium text-gray-700">
                  License ID
                </label>
                <div className="relative mt-2">
                  <input
                    id="canliiApiKey"
                    type={showCanliiApiKey ? "text" : "password"}
                    value={canliiApiKey}
                    onChange={(e) => setCanliiApiKey(e.target.value)}
                    disabled={!isEditingCanlii || isSavingCanlii}
                    placeholder="Enter Canlii API key"
                    className={`w-full px-4 py-2 pr-10 text-sm text-gray-700 bg-white border 
                      ${canliiError ? "border-red-500" : "border-gray-200"}
                      rounded-lg transition-all duration-200 
                      ${
                        isEditingCanlii
                          ? "focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200"
                          : "disabled:cursor-not-allowed disabled:bg-gray-200"
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCanliiApiKey(!showCanliiApiKey)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                  >
                    {showCanliiApiKey ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
                {canliiError && (
                  <p className="mt-1 text-xs text-red-500">{canliiError}</p>
                )}
              </div>

              <div className="flex justify-end mt-4 space-x-2">
                {isEditingCanlii ? (
                  <>
                    <button
                      onClick={handleCanliiSave}
                      disabled={isSavingCanlii}
                      className="inline-flex justify-center items-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-white bg-violet-600
                        hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingCanlii ? (
                        <>
                          <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                    <button
                      onClick={handleCanliiCancel}
                      disabled={isSavingCanlii}
                      className="inline-flex justify-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-200
                        hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleCanliiEdit}
                    className="inline-flex justify-center py-2 px-4 border border-transparent
                      shadow-sm text-sm font-medium rounded-md text-white bg-violet-600
                      hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                      focus:ring-violet-500"
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </DisclosurePanel>
      </Disclosure>

      {/* Alpha Vantage License Management Section */}
      <Disclosure as="div" className="py-2" defaultOpen>
        <DisclosureButton className="group flex w-full items-center justify-between">
          <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
            Alpha Vantage License
          </span>
          <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
        </DisclosureButton>
        <DisclosurePanel className="mt-4 text-sm text-gray-600">
          {isLoadingAlphaVantage ? (
            <div className="flex items-center space-x-2">
              <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                <label htmlFor="alphaVantageApiKey" className="block text-sm font-medium text-gray-700">
                  License ID
                </label>
                <div className="relative mt-2">
                  <input
                    id="alphaVantageApiKey"
                    type={showAlphaVantageApiKey ? "text" : "password"}
                    value={alphaVantageApiKey}
                    onChange={(e) => setAlphaVantageApiKey(e.target.value)}
                    disabled={!isEditingAlphaVantage || isSavingAlphaVantage}
                    placeholder="Enter Alpha Vantage API key"
                    className={`w-full px-4 py-2 pr-10 text-sm text-gray-700 bg-white border 
                      ${alphaVantageError ? "border-red-500" : "border-gray-200"}
                      rounded-lg transition-all duration-200 
                      ${
                        isEditingAlphaVantage
                          ? "focus:border-violet-500 focus:outline-none focus:ring focus:ring-violet-200"
                          : "disabled:cursor-not-allowed disabled:bg-gray-200"
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowAlphaVantageApiKey(!showAlphaVantageApiKey)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                  >
                    {showAlphaVantageApiKey ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
                {alphaVantageError && (
                  <p className="mt-1 text-xs text-red-500">{alphaVantageError}</p>
                )}
              </div>

              <div className="flex justify-end mt-4 space-x-2">
                {isEditingAlphaVantage ? (
                  <>
                    <button
                      onClick={handleAlphaVantageSave}
                      disabled={isSavingAlphaVantage}
                      className="inline-flex justify-center items-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-white bg-violet-600
                        hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingAlphaVantage ? (
                        <>
                          <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                    <button
                      onClick={handleAlphaVantageCancel}
                      disabled={isSavingAlphaVantage}
                      className="inline-flex justify-center py-2 px-4 border border-transparent
                        shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-200
                        hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2
                        focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleAlphaVantageEdit}
                    className="inline-flex justify-center py-2 px-4 border border-transparent
                      shadow-sm text-sm font-medium rounded-md text-white bg-violet-600
                      hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                      focus:ring-violet-500"
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </DisclosurePanel>
      </Disclosure>
    </div>
  );
};

export default License;